import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import WhatsAppWidget from "./WhatsAppWidget";
import LanguageSwitcher from "./LanguageSwitcher";
// import CurrencySwitcher from "./CurrencySwitcher";
import { useTranslation } from "react-i18next";

export default function Layout({ children }: { children: React.ReactNode }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: t("nav.home"), href: "/" },
    { name: t("nav.tours"), href: "/experiences" },
    { name: t("nav.vipConcierge"), href: "/vip-transfers" },
    { name: t("nav.about"), href: "/about" },
    { name: t("nav.blog", "Blog"), href: "/blog" },
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans" dir={i18n.dir()}>
      {/* Header */}
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 border-b border-transparent transition-all duration-300 ease-in-out",
          isScrolled
            ? "bg-primary/95 backdrop-blur-md py-3 shadow-[0_12px_30px_rgb(4_18_34_/_28%)] border-secondary/30"
            : "bg-transparent py-6"
        )}
      >
        <div className="container flex justify-between items-center">
          {/* Logo */}
          <Link href="/">
            <div className="flex items-center gap-3 group cursor-pointer">
              <div className="relative h-12 w-auto overflow-hidden rounded-xl border border-white/60 bg-white/95 p-1 shadow-[0_8px_20px_rgb(4_18_34_/_18%)] transition-colors">
                <img
                  src="/manus-storage/istanbul-escape-logo_2e866b36.webp"
                  alt="Istanbul Escape Logo"
                  width="160"
                  height="160"
                  decoding="async"
                  className="h-full w-auto rounded-lg object-contain"
                />
              </div>
              <div className="hidden xl:flex flex-col gap-1">
                <span className="brand-wordmark">Istanbul Escape</span>
                <span className="brand-tagline">{t("brand.tagline")}</span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="nav-frame hidden items-center gap-1 rounded-full p-1 lg:flex">
            {navLinks.map(link => (
              <Link key={link.href} href={link.href}>
                <div
                  className={cn(
                    "relative rounded-full px-4 py-2 text-sm font-medium tracking-wide transition-all duration-300 cursor-pointer group overflow-hidden",
                    isScrolled
                      ? "text-primary-foreground/90 hover:bg-white/10"
                      : "text-white/90 hover:bg-white/10",
                    location === link.href &&
                      (isScrolled
                        ? "bg-white/10 text-secondary font-bold"
                        : "bg-white/20 text-white font-bold")
                  )}
                >
                  <span className="relative z-10">{link.name}</span>
                  {location === link.href && (
                    <span className="nav-active-marker absolute inset-0 rounded-full border border-secondary/45"></span>
                  )}
                </div>
              </Link>
            ))}

            <LanguageSwitcher />
            <Link href="/contact" className="ml-6">
              <Button
                variant="outline"
                size="lg"
                className={cn(
                  "border-secondary/70 bg-secondary text-primary hover:bg-secondary/90 hover:text-primary transition-all duration-300 rounded-full px-6",
                  !isScrolled &&
                    "bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-primary"
                )}
              >
                {t("nav.bookNow")}
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden z-50 relative">
            <LanguageSwitcher />
            <button
              className="menu-trigger text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X
                  className={cn(
                    "w-6 h-6",
                    isScrolled ? "text-primary-foreground" : "text-white"
                  )}
                />
              ) : (
                <Menu
                  className={cn(
                    "w-6 h-6",
                    isScrolled ? "text-primary-foreground" : "text-white"
                  )}
                />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-primary z-40 lg:hidden transition-transform duration-300 ease-in-out flex flex-col justify-center items-center gap-6",
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
        style={{ top: "0" }}
      >
        {navLinks.map(link => (
          <Link key={link.name} href={link.href}>
            <div
              className={cn(
                "text-xl font-serif transition-colors cursor-pointer px-6 py-2 rounded-full border border-transparent",
                location === link.href
                  ? "bg-white/10 text-secondary font-bold"
                  : "text-white hover:text-secondary"
              )}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.name}
            </div>
          </Link>
        ))}
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
        </div>
        <Link href="/contact">
          <Button
            size="lg"
            className="bg-secondary text-primary hover:bg-secondary/90 mt-4 rounded-full px-8"
          >
            {t("nav.bookNow")}
          </Button>
        </Link>
      </div>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      <WhatsAppWidget />

      {/* Footer */}
      <footer className="bg-primary text-white pt-20 pb-10 border-t border-secondary/30">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            {/* Brand Column */}
            <div className="col-span-1 md:col-span-1">
              <Link href="/">
                <div className="flex items-center gap-3 mb-6 cursor-pointer">
                  <div className="relative h-12 w-auto overflow-hidden rounded-lg bg-white p-1">
                    <img
                      src="/manus-storage/istanbul-escape-logo_2e866b36.webp"
                      alt="Istanbul Escape Logo"
                      width="160"
                      height="160"
                      loading="lazy"
                      decoding="async"
                      className="h-full w-auto object-contain"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="brand-wordmark">Istanbul Escape</span>
                    <span className="brand-tagline">{t("brand.tagline")}</span>
                  </div>
                </div>
              </Link>
              <p className="text-[#AAB7C4] text-sm leading-relaxed mb-6">
                {t("footer.description")}
              </p>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/escapeistanbul/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit Istanbul Escape on Instagram"
                  className="text-white/60 hover:text-secondary transition-colors"
                >
                  <Instagram size={20} />
                </a>
              </div>
            </div>

            {/* Links Column 2 */}
            <div>
              <h4 className="font-serif text-lg mb-6 text-secondary">
                {t("footer.services")}
              </h4>
              <ul className="space-y-3 text-sm text-[#AAB7C4]">
                <li>
                  <Link
                    href="/experiences"
                    className="footer-link hover:text-white"
                  >
                    {t("nav.tours")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/vip-transfers"
                    className="footer-link hover:text-white"
                  >
                    {t("nav.vipConcierge")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="/contact"
                    className="footer-link hover:text-white"
                  >
                    {t("nav.tripPlanning")}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="font-serif text-lg mb-6 text-secondary">
                {t("footer.contact")}
              </h4>
              <ul className="space-y-3 text-sm text-[#AAB7C4]">
                <li>
                  <a
                    href="mailto:info@istanbulescape.com"
                    className="hover:text-white transition-colors"
                  >
                    info@istanbulescape.com
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+905352257703"
                    className="hover:text-white transition-colors"
                  >
                    TR: +90 535 225 77 03
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+447475779555"
                    className="hover:text-white transition-colors"
                  >
                    UK: +44 7475 779555
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+12392956176"
                    className="hover:text-white transition-colors"
                  >
                    USA: +1 239 2956176
                  </a>
                </li>
                <li>{t("footer.location")}</li>
              </ul>
              <Link href="/contact">
                <Button className="mt-6 w-full bg-secondary text-primary hover:bg-secondary/90 rounded-full">
                  {t("nav.contact")}
                </Button>
              </Link>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/65">
            <div className="flex flex-col md:flex-row gap-2 items-center">
              <p>
                &copy; {new Date().getFullYear()} Istanbul Escape.{" "}
                {t("footer.rights")}
              </p>
              <span className="hidden md:inline mx-2">|</span>
              <a
                href="https://techdataweb.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                {t("footer.madeBy")} TechDataWeb
              </a>
            </div>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">
                {t("footer.privacy")}
              </a>
              <a href="#" className="hover:text-white transition-colors">
                {t("footer.terms")}
              </a>
              <a href="#" className="hover:text-white transition-colors">
                {t("footer.cookies")}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
